import { ICommandBarItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBar } from 'baseComponents';
import { getCommandBarItemsReportContextMenu } from 'components/Reports/ReportContextMenu';
import {
  approvalPAIcon,
  complianceIcon,
  deleteIcon,
  discardIcon,
  leftArrowIcon,
  rightArrowIcon,
  saveIcon,
} from 'globalStyles';
import { TFunction } from 'i18next';
import ReportDefinition, { ReportContext } from 'models/reports/ReportDefinition';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasSubscriptionFeature } from 'services/Auth/featurePermissions';

interface ITabDetailsCommandBar {
  saveDisabled?: boolean;
  discardDisabled?: boolean;
  removeDisabled?: boolean;
  openDpiaAnalysisDisabled?: boolean;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  onRemove: () => void;
  onDiscard: () => void;
  onSave: () => void;
  onOpenDpiaAnalysis: () => void;
  onPrevProcessingActivity: () => void;
  onNextProcessingActivity: () => void;
  setSelectedReport: (report: ReportDefinition) => void;
  complianceDisabled: boolean;
  onComplianceClick: () => void;
  showApprovalButton: boolean;
  onClickApproval: () => void;
  approvalDisabled: boolean;
  // the scene's AI actions, last on the bar (useSceneAIActionsItem)
  // the item's Assess menu (PLAN-075, useAssessmentCommand), before Previous and Next
  assessItem?: ICommandBarItemProps;
  aiItem?: ICommandBarItemProps;
}

export const TabDetailsCommandBar = (props: ITabDetailsCommandBar) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'reports', 'processingActivity']);

  const buttons: ICommandBarItemProps[] = [
    {
      key: 'save',
      text: t('translation:General.Button.Save'),
      iconProps: saveIcon,
      disabled: props.saveDisabled,
      onClick: () => props.onSave(),
    },
    {
      key: 'discard',
      text: t('translation:General.Button.Discard'),
      disabled: props.discardDisabled,
      iconProps: discardIcon,
      onClick: props.onDiscard,
    },
    {
      key: 'remove',
      text: t('translation:General.Button.Remove'),
      iconProps: deleteIcon,
      disabled: props.removeDisabled,
      onClick: props.onRemove,
    },
    {
      key: 'nextactions',
      text: t('processingActivity:CommandBar.NextActions'),
      iconProps: { iconName: 'ActionCenter' },
      subMenuProps: {
        items: [
          {
            key: 'compliance',
            text: t('processingActivity:CommandBar.Compliance'),
            iconProps: complianceIcon,
            disabled: props.complianceDisabled,
            onClick: props.onComplianceClick,
            title: props.complianceDisabled ? t('processingActivity:CommandBar.ComplianceDisabledHover') : undefined,
          },
        ],
      },
    },
    getCommandBarItemsReportContextMenu(
      ReportContext.ProcessingActivityDetails,
      {
        disabled: false,
        onSelect: (report) => props.setSelectedReport(report),
      },
      t as TFunction<string[]>,
    ),
    {
      key: 'prev',
      text: t('translation:General.Button.Prev'),
      iconProps: leftArrowIcon,
      disabled: props.prevDisabled,
      onClick: props.onPrevProcessingActivity,
    },
    {
      key: 'next',
      text: t('translation:General.Button.Next'),
      iconProps: rightArrowIcon,
      disabled: props.nextDisabled,
      onClick: props.onNextProcessingActivity,
    },
  ];

  if (hasSubscriptionFeature(appContext.user.login.subscriptionType, FeatureTypes.ProcessingActivitiesDpia)) {
    buttons[3].subMenuProps?.items.push({
      key: 'analysis',
      text: t('processingActivity:CommandBar.DpiaAnalysis'),
      iconProps: { iconName: 'AnalyticsLogo' },
      disabled: props.openDpiaAnalysisDisabled,
      onClick: props.onOpenDpiaAnalysis,
    });
  }

  if (props.showApprovalButton) {
    buttons[3].subMenuProps?.items.push({
      key: 'approval',
      text: t('processingActivity:CommandBar.Approval'),
      disabled: props.approvalDisabled,
      iconProps: approvalPAIcon,
      onClick: props.onClickApproval,
    });
  }

  //before Previous and Next, after the menus above have their items by index
  if (props.assessItem) buttons.splice(buttons.findIndex((button) => button.key === 'prev'), 0, props.assessItem);
  if (props.aiItem) buttons.push(props.aiItem);

  return <IPCommandBar items={buttons} styles={{ root: { padding: 0 } }} />;
};
